import { useSelector } from "react-redux";

import { PricingTypeId } from "constants/pricingTypes";

import { commissionFormatter } from "lib/agGrid/formatters";
import { formatDollarsFromCents, formatPercentage, formatWeightKg } from "lib";
import {
  getVendorCommissionSourceLabel,
  resolveEffectiveVendorCommission,
} from "lib/vendorCommission";

import {
  getActiveLivestockAgentDeployment,
  getBuyerByEContractAgreementId,
  getConsignments,
  getCurrentSale,
  getCurrentSpeciesId,
  getDefaultBillingSettings,
  getDeploymentAgeOptionsByDeploymentIdAndSpeciesId,
  getDeploymentBreedOptionsByDeploymentIdAndSpeciesId,
  getProperties,
  getSaleLotIdsByAgreementId,
  getSaleLots,
  getSexes,
  getVendorByEContractAgreementId,
  selectResolvedInterestSettingsByBusinessIdLookup,
  getSaleVendorCommissionByDeploymentId,
  getVendorCommissionForCurrentSaleByBusinessId,
  getVendorCommissionForCurrentSaleByDeploymentId,
} from "selectors";
import { getBusinessRegistrationById } from "selectors/businessRegistrations";

export function computeLotDisplayValues(
  lot: SaleLot,
  sexes: Record<string | number, { name?: string }>,
  ageOptions: { value: number; label: string }[],
  breedOptions: { value: number; label: string }[],
): { description: string; unitPrice: string | null; weightKg: string | null } {
  const sexLabel = lot.sex_id != null ? sexes[lot.sex_id]?.name ?? null : null;
  const ageLabel =
    lot.age_id != null
      ? ageOptions.find(a => a.value === lot.age_id)?.label ?? null
      : null;
  const breedLabel =
    lot.breed_id != null
      ? breedOptions.find(b => b.value === lot.breed_id)?.label ?? null
      : null;
  const description =
    [ageLabel, breedLabel, sexLabel].filter(Boolean).join(", ") || "-";
  const isPerKilo = lot.pricing_type_id === PricingTypeId.PER_KILO;
  const unitPrice =
    lot.cents_per_head > 0
      ? `${formatDollarsFromCents(lot.cents_per_head)}${isPerKilo ? "/kg" : "/hd"}`
      : null;
  const weightKg =
    lot.total_mass_grams > 0
      ? formatWeightKg(lot.total_mass_grams, true)
      : null;
  return { description, unitPrice, weightKg };
}

export function useEContractReviewData(agreementId: string) {
  const vendor = useSelector(getVendorByEContractAgreementId(agreementId));
  const vendorRegistration = useSelector(
    getBusinessRegistrationById(vendor?.registrationId),
  );
  const buyer = useSelector(getBuyerByEContractAgreementId(agreementId));
  const saleLotIds = useSelector(getSaleLotIdsByAgreementId(agreementId));
  const allSaleLots = useSelector(getSaleLots);
  const lots = saleLotIds
    .map(id => (allSaleLots as Record<string, SaleLot | undefined>)[id])
    .filter((l): l is SaleLot => l != null);

  const deployment = useSelector(getActiveLivestockAgentDeployment);
  const speciesId = useSelector(getCurrentSpeciesId);
  const deploymentId = deployment?.id;
  const sale = useSelector(getCurrentSale);

  const consignments = useSelector(getConsignments);
  const properties = useSelector(getProperties);
  const sexes = useSelector(getSexes);
  const ageOptions = useSelector(
    getDeploymentAgeOptionsByDeploymentIdAndSpeciesId(deploymentId, speciesId),
  );
  const breedOptions = useSelector(
    getDeploymentBreedOptionsByDeploymentIdAndSpeciesId(
      deploymentId,
      speciesId,
    ),
  );

  const deploymentCommission: VendorCommissionBand | null = useSelector(
    getVendorCommissionForCurrentSaleByDeploymentId(deploymentId),
  );
  const saleCommission: Commission | null = useSelector(
    getSaleVendorCommissionByDeploymentId(deploymentId),
  );
  const vendorCommission: VendorCommissionBand | null = useSelector(
    getVendorCommissionForCurrentSaleByBusinessId(vendor?.id),
  );

  const billingSettings = useSelector(getDefaultBillingSettings);
  const resolvedInterestSettings = useSelector(
    selectResolvedInterestSettingsByBusinessIdLookup,
  );

  // Collect originating/destination PICs, NVDs, and delivery dates from lots
  const originatingPICs = new Set<string>();
  const destinationPICs = new Set<string>();
  const nvds = new Set<string>();
  const deliveryDates = new Set<string>();
  const commissionFormattedValues = new Set<string>();
  const commissionSourceLabels = new Set<string>();

  for (const lot of lots) {
    if (lot.destination_property_id) {
      const destProp = properties[lot.destination_property_id];
      if (destProp?.PIC) destinationPICs.add(destProp.PIC);
    }

    const consignment = consignments[lot.consignment_id];
    if (!consignment) continue;

    const vendorProp = consignment.vendor_property_id
      ? properties[consignment.vendor_property_id]
      : null;
    if (vendorProp?.PIC) originatingPICs.add(vendorProp.PIC);
    if (consignment.NVD) nvds.add(consignment.NVD);
    const deliveryDate = consignment.carrierCharge?.deliveryDate;
    if (deliveryDate) deliveryDates.add(deliveryDate);

    const effectiveCommission = resolveEffectiveVendorCommission({
      consignmentCommission: consignment.vendorCommission,
      deploymentCommission,
      saleCommission,
      vendorCommission,
    });
    if (effectiveCommission) {
      const { commission, source } = effectiveCommission;
      commissionFormattedValues.add(commissionFormatter(commission));
      commissionSourceLabels.add(getVendorCommissionSourceLabel(source));
    }
  }

  const singleDeliveryDate =
    deliveryDates.size === 1 ? Array.from(deliveryDates)[0] : null;

  const buyerInterestSettingsList = buyer
    ? resolvedInterestSettings?.[buyer.id]
    : null;
  const buyerInterestSettings = Array.isArray(buyerInterestSettingsList)
    ? buyerInterestSettingsList[0]
    : buyerInterestSettingsList;
  const interestDisplay =
    billingSettings?.enableInterest &&
    buyerInterestSettings?.debitInterestRate != null
      ? formatPercentage(buyerInterestSettings.debitInterestRate, {
          decimalPlaces: 2,
          roundingMethod: null,
        })
      : null;

  const agentAddress = deployment?.deploymentSettings?.physicalAddress ?? null;

  return {
    vendor,
    vendorRegistration,
    buyer,
    lots,
    sale,
    consignments,
    sexes,
    ageOptions,
    breedOptions,
    originatingPICs: Array.from(originatingPICs),
    destinationPICs: Array.from(destinationPICs),
    nvds: Array.from(nvds),
    singleDeliveryDate,
    commissionDisplay: commissionFormattedValues.size
      ? [...commissionFormattedValues].sort().join(", ")
      : null,
    commissionSourceDisplay: commissionSourceLabels.size
      ? [...commissionSourceLabels].sort().join(", ")
      : null,
    interestDisplay,
    agentAddress,
  };
}
